import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, renderList as _renderList, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex" }
const _hoisted_2 = { class: "w-full font-semibold" }
const _hoisted_3 = { class: "grid grid-cols-2 w-full gap-4" }
const _hoisted_4 = {
  key: 0,
  class: "flex flex-col items-baseline gap-4 w-1/4 p-6 border-l border-gray-300"
}
const _hoisted_5 = { class: "w-full font-semibold" }
const _hoisted_6 = { class: "border-t border-gray-300 p-6" }
const _hoisted_7 = { class: "grid grid-cols-4 gap-4 w-full" }
const _hoisted_8 = {
  key: 1,
  class: "border-t border-gray-300"
}
const _hoisted_9 = { class: "flex flex-col w-full items-baseline gap-4 p-6" }
const _hoisted_10 = { class: "w-full font-semibold" }
const _hoisted_11 = { class: "grid grid-cols-4 gap-x-4 w-full" }
const _hoisted_12 = { class: "mb-4 border-t border-gray-300 bg-gray-50" }
const _hoisted_13 = { class: "flex flex-wrap gap-x-2.5 border-b border-gray-300 bg-white pt-4" }
const _hoisted_14 = { class: "mb-2 md:mb-0 md:mt-1 md:mr-4 ml-6 text-center md:text-left w-full md:w-fit" }
const _hoisted_15 = {
  key: 0,
  class: "hidden md:flex border-l border-gray-300 ml-2 mr-4 mb-3 -mt-1"
}
const _hoisted_16 = {
  key: 0,
  class: "w-full grid md:grid-cols-12 gap-x-6"
}
const _hoisted_17 = {
  key: 1,
  class: "w-full grid md:grid-cols-12 gap-x-6"
}
const _hoisted_18 = {
  key: 2,
  class: "w-full grid md:grid-cols-12 gap-x-6"
}
const _hoisted_19 = {
  key: 3,
  class: "w-full grid md:grid-cols-12 gap-x-6"
}

import {
  MedKitButton,
  MedKitDateTimeField,
  MedKitNumberField,
  MedKitRadioGroup,
  MedKitSearch,
  MedKitSelect,
  MedKitTextField
} from '@bigpicturemedical/medkit'
import {
  DvCodedTextFilter,
  DvCodedText,
  TerminologySearch,
  DvCodedTextList,
  DvCount,
  DvDateTime,
  DvDuration,
  DvQuantity,
  DvText,
  NodeRepeater,
  IPath,
  INode,
  useWebTemplateHelpers,
  find,
  MultiNodeRepeater,
  HeadlessRepeater
} from '@bigpicturemedical/openehr-vue'
import { PropType, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  BlockContainer,
  BlockHeader,
  BlockPanel,
  BlockPanelHeader,
  BlockWell
} from './_layout'


export default /*#__PURE__*/_defineComponent({
  __name: 'PresentingComplaintBlock',
  props: {
  path: {
    type: Object as PropType<IPath>,
    required: true
  },
  name: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  errors: {
    type: Array as PropType<Array<string>>,
    default: () => []
  }
},
  setup(__props) {

const props = __props;



const { t } = useI18n()
const { nodeExists, getData, updateData, template, language } = useWebTemplateHelpers()

onMounted(() => {
  setSymptomSignForComplaint(0)
  setTimeForComplaint(0)
})

const getUnitOptions = (min, max, step = 1) => {
  let i = 0
  const options = []
  while (i < max) {
    const value = (i + Number(step))
    options.push({ value, label: `${value} / ${max}` })
    i++
  }
  return options
}
const symptomSignNode = find(template.value, props.path.add('any_event.symptom_sign.symptom_sign_name')) as INode
const symptomSignInput = symptomSignNode.inputs.find(input => input.suffix === 'code')

const setSymptomSignForComplaint  = (index) => {
  updateData(
    props.path.add(`any_event.${index}.symptom_sign.symptom_sign_name`),
    {
      '|code': symptomSignInput.list[0].value,
      '|value': symptomSignInput.list[0].localizedLabels?.[language.value] ?? symptomSignInput.list[0].label,
      '|terminology': symptomSignInput.terminology
    }
  )
}

const setTimeForComplaint = (index) => {
  updateData(props.path.add(`any_event.${index}.time`), (new Date()).toISOString())
}

const addNewComplaint = (addItem) => {
  addItem()

  const complaints: Array<object> = getData(props.path.add('any_event'))
  if (symptomSignInput.list.length >= 1) {
    setSymptomSignForComplaint(complaints.length - 1)
  }

  setTimeForComplaint(complaints.length - 1)
}

const factorTypes = {
  precipitatingFactor: 'precipitating_factor',
  modifyingFactor: 'modifying_factor',
  resolvingFactor: 'resolving_factor',
  associatedSymptoms: 'symptom_sign'
}

const getFactorTypeBorderColour = (factorType: string) => {
  switch (factorType) {
  case factorTypes.precipitatingFactor:
    return 'border-blue-400'
  case factorTypes.modifyingFactor:
    return 'border-yellow-400'
  case factorTypes.resolvingFactor:
    return 'border-green-400'
  case factorTypes.associatedSymptoms:
    return 'border-gray-300'
  default:
    return 'border-gray-300'
  }
}

const getFactorTypeNodes = (eventIndex: number) => {
  const nodes = []
  if (nodeExists(props.path.add(`any_event.${eventIndex}.symptom_sign.precipitating_factor`))) {
    nodes.push({
      path: props.path.add(`any_event.${eventIndex}.symptom_sign.precipitating_factor`),
      buttonLabel: 'Precipitating Factor',
      type: factorTypes.precipitatingFactor
    })
  }
  if (nodeExists(props.path.add(`any_event.${eventIndex}.symptom_sign.modifying_factor`))) {
    nodes.push({
      path: props.path.add(`any_event.${eventIndex}.symptom_sign.modifying_factor`),
      buttonLabel: 'Modifying Factor',
      type: factorTypes.modifyingFactor
    })
  }
  if (nodeExists(props.path.add(`any_event.${eventIndex}.symptom_sign.resolving_factor`))) {
    nodes.push({
      path: props.path.add(`any_event.${eventIndex}.symptom_sign.resolving_factor`),
      buttonLabel: 'Resolving Factor',
      type: factorTypes.resolvingFactor
    })
  }
  if (nodeExists(props.path.add(`any_event.${eventIndex}.symptom_sign.symptom_sign`))) {
    nodes.push({
      path: props.path.add(`any_event.${eventIndex}.symptom_sign.symptom_sign`),
      buttonLabel: 'Associated Symptom/Sign',
      type: factorTypes.associatedSymptoms
    })
  }
  return nodes
}

const isAnatomicalLocationARepeater = computed(() => {
  if (!nodeExists(props.path.add('any_event.symptom_sign.anatomical_location'))) {
    return false
  }
  return (find(template.value, props.path.add('any_event.symptom_sign.anatomical_location')) as INode).max !== 1
})

const isBodySiteNameTheOnlyAnatomicalLocationField = computed(() => {
  return nodeExists(props.path.add('any_event.symptom_sign.anatomical_location.body_site_name'))
      && !nodeExists(props.path.add('any_event.symptom_sign.anatomical_location.laterality'))
      && !nodeExists(props.path.add('any_event.symptom_sign.anatomical_location.aspect'))
      && !nodeExists(props.path.add('any_event.symptom_sign.anatomical_location.anatomical_line'))
})

const isCharacterFieldMultiselect = computed(() => {
  if (!nodeExists(props.path.add('any_event.symptom_sign.character'))) {
    return false
  }
  return (find(template.value, props.path.add('any_event.symptom_sign.character')) as INode).max !== 1
})

const addPath = (eventIndex: number, nodeName: string) => props.path.add(`any_event.${eventIndex}.symptom_sign.${nodeName}`)
const hasNode = (eventIndex: number, nodeName: string) => nodeExists(addPath(eventIndex, nodeName))
const hasSomeNodes = (eventIndex: number, nodeNames: string[] = []) => nodeNames.some(nodeName => hasNode(eventIndex, nodeName))

const characterisationNodes = {
  'description': hasNode(0, 'description'),
  'onset_type': hasNode(0, 'onset_type'),
  'severity': hasNode(0, 'severity_category') || hasNode(0, 'severity_rating'),
  'character': hasNode(0, 'character'),
  'progression': hasNode(0, 'progression'),
  'pattern': hasNode(0, 'pattern'),
  'impact': hasNode(0, 'impact'),
}

const isLastColumn = (nodeName: string): boolean => {
  const nodeIndex = Object.keys(characterisationNodes).indexOf(nodeName)
  const nodesAfter = Object.values(characterisationNodes).slice(nodeIndex)
  return nodeIndex === (Object.keys(characterisationNodes).length - 1) || nodesAfter.filter(Boolean).length === 1
}

const getColumnSpan = (nodeName: string): string => {
  const characterisationNodeCount = Object.values(characterisationNodes).filter(Boolean).length
  const lowColumnCount = characterisationNodeCount < 4
  const oddColumnCount = characterisationNodeCount % 2 === 1
  return (isLastColumn(nodeName) && oddColumnCount) || lowColumnCount ? 'col-span-2' : 'col-span-1'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BlockContainer), { class: "space-y-6" }, {
    default: _withCtx(({ display }) => [
      _createVNode(_unref(BlockHeader), null, {
        heading: _withCtx(() => [
          _createTextVNode(_toDisplayString(props.name), 1)
        ]),
        description: _withCtx(() => [
          _createTextVNode(_toDisplayString(props.description), 1)
        ]),
        _: 1
      }),
      _createVNode(_unref(NodeRepeater), {
        path: props.path.add('any_event'),
        reverse: ""
      }, {
        header: _withCtx(({ addItem }) => [
          _createVNode(_unref(MedKitButton), {
            class: "w-full",
            kind: "repeater",
            label: _unref(t)('presentingComplaint.stories.add'),
            icon: "plus",
            "icon-position": "start",
            onClick: ($event: any) => (addNewComplaint(addItem))
          }, null, 8, ["label", "onClick"])
        ]),
        default: _withCtx(({ index: anyEventIndex, remove, count }) => [
          _createVNode(_unref(BlockPanel), null, {
            default: _withCtx(() => [
              _createVNode(_unref(BlockPanelHeader), {
                title: _unref(getData)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign_name`))?.['|value']
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(MedKitButton), {
                    class: _normalizeClass({
                'invisible': count < 2
              }),
                    size: "small",
                    label: _unref(t)('presentingComplaint.stories.remove'),
                    "icon-position": "only",
                    icon: "delete",
                    onClick: remove
                  }, null, 8, ["class", "label", "onClick"])
                ]),
                _: 2
              }, 1032, ["title"]),
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", {
                  class: _normalizeClass(["flex flex-col items-baseline gap-4 p-6", [ hasSomeNodes(anyEventIndex, ['episode_onset', 'episode_resolution', 'duration', 'episodicity']) ? 'w-3/4' : 'w-full' ]])
                }, [
                  _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('presentingComplaint.story.characterisationOfSymptomSign.title')), 1),
                  _createElementVNode("div", _hoisted_3, [
                    (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.description`)))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 0,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.description`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('description')),
                              placeholder: _unref(t)('presentingComplaint.story.description.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.onset_type`)))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 1,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.onset_type`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('onset_type')),
                              placeholder: _unref(t)('presentingComplaint.story.onsetType.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (hasSomeNodes(anyEventIndex, ['severity_category', 'severity_rating']))
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 2,
                          class: _normalizeClass(["flex gap-4 w-full", getColumnSpan('severity')])
                        }, [
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_category`)))
                            ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                key: 0,
                                path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_category`)
                              }, {
                                default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                  _createVNode(_unref(MedKitSelect), {
                                    class: _normalizeClass([
                        _unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_rating`)) ? 'w-1/2' : 'w-full',
                      ]),
                                    placeholder: _unref(t)('presentingComplaint.story.severityCategory.placeholder'),
                                    error: fieldErrors[0],
                                    label: fieldName,
                                    "model-value": selected,
                                    options: options,
                                    required: required,
                                    "onUpdate:modelValue": setSelected,
                                    onInput: validate
                                  }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                ]),
                                _: 2
                              }, 1032, ["path"]))
                            : _createCommentVNode("", true),
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_rating`)))
                            ? (_openBlock(), _createBlock(_unref(DvQuantity), {
                                key: 1,
                                path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_rating`)
                              }, {
                                default: _withCtx(({ name: fieldName, min, max, magnitude, unit, setMagnitude, validate, errors: fieldErrors, required }) => [
                                  _createVNode(_unref(MedKitSelect), {
                                    class: _normalizeClass([
                        _unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.severity_category`)) ? 'w-1/2' : 'w-full',
                      ]),
                                    placeholder: _unref(t)('presentingComplaint.story.severityRating.placeholder'),
                                    error: fieldErrors[0],
                                    label: fieldName,
                                    "model-value": magnitude,
                                    options: getUnitOptions(min, max, unit),
                                    required: required,
                                    "onUpdate:modelValue": setMagnitude,
                                    onInput: validate
                                  }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                ]),
                                _: 2
                              }, 1032, ["path"]))
                            : _createCommentVNode("", true)
                        ], 2))
                      : _createCommentVNode("", true),
                    (_unref(isCharacterFieldMultiselect) && hasNode(anyEventIndex, 'character'))
                      ? (_openBlock(), _createBlock(_unref(DvCodedTextList), {
                          key: 3,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.character`)
                        }, {
                          default: _withCtx(({ name: fieldName, values, setValues, items, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(DvCodedTextFilter), { items: items }, {
                              default: _withCtx(({ filter, results }) => [
                                _createVNode(_unref(MedKitSearch), {
                                  class: "flex-1",
                                  multiple: "",
                                  "model-value": values.map(value => ({
                        label: value['|value'],
                        value: value,
                      })),
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  options: results.map(result => ({
                        label: result['|value'],
                        value: result,
                      })),
                                  placeholder: _unref(t)('presentingComplaint.story.character.placeholder'),
                                  required: required,
                                  "onUpdate:query": filter,
                                  "onUpdate:modelValue": ($event: any) => (setValues($event.map(value => value.value))),
                                  onInput: validate
                                }, null, 8, ["model-value", "error", "label", "options", "placeholder", "required", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["items"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (!_unref(isCharacterFieldMultiselect) && hasNode(anyEventIndex, 'character'))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 4,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.character`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('character')),
                              placeholder: _unref(t)('presentingComplaint.story.character.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.progression`)))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 5,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.progression`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('progression')),
                              placeholder: _unref(t)('presentingComplaint.story.progression.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.pattern`)))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 6,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.pattern`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('pattern')),
                              placeholder: _unref(t)('presentingComplaint.story.pattern.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true),
                    (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.impact`)))
                      ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                          key: 7,
                          path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.impact`)
                        }, {
                          default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                            _createVNode(_unref(MedKitSelect), {
                              class: _normalizeClass(getColumnSpan('impact')),
                              placeholder: _unref(t)('presentingComplaint.story.impact.placeholder'),
                              error: fieldErrors[0],
                              label: fieldName,
                              "model-value": selected,
                              options: options,
                              required: required,
                              "onUpdate:modelValue": setSelected,
                              onInput: validate
                            }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                          ]),
                          _: 2
                        }, 1032, ["path"]))
                      : _createCommentVNode("", true)
                  ])
                ], 2),
                (hasSomeNodes(anyEventIndex, ['episode_onset', 'episode_resolution', 'duration', 'episodicity']))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)('presentingComplaint.story.whenAndHowLong.title')), 1),
                      (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.episode_onset`)))
                        ? (_openBlock(), _createBlock(_unref(DvDateTime), {
                            key: 0,
                            path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.episode_onset`)
                          }, {
                            default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                              _createVNode(_unref(MedKitDateTimeField), {
                                class: "w-full",
                                error: fieldErrors[0],
                                label: fieldName,
                                "model-value": value,
                                required: required,
                                "onUpdate:modelValue": setValue,
                                onInput: validateIfAlreadyValidated,
                                onBlur: validate
                              }, null, 8, ["error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                            ]),
                            _: 2
                          }, 1032, ["path"]))
                        : _createCommentVNode("", true),
                      (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.episode_resolution`)))
                        ? (_openBlock(), _createBlock(_unref(DvDateTime), {
                            key: 1,
                            path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.episode_resolution`)
                          }, {
                            default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                              _createVNode(_unref(MedKitDateTimeField), {
                                class: "w-full",
                                error: fieldErrors[0],
                                label: fieldName,
                                "model-value": value,
                                required: required,
                                "onUpdate:modelValue": setValue,
                                onInput: validateIfAlreadyValidated,
                                onBlur: validate
                              }, null, 8, ["error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                            ]),
                            _: 2
                          }, 1032, ["path"]))
                        : _createCommentVNode("", true),
                      (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.duration`)))
                        ? (_openBlock(), _createBlock(_unref(DvDuration), {
                            key: 2,
                            path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.duration`)
                          }, {
                            default: _withCtx(({
                  name: fieldName,
                  errors: fieldErrors,
                  validate,
                  validateIfAlreadyValidated,
                  periodOptions,
                  setDuration,
                  setPeriod,
                  duration,
                  period,
                  required
                }) => [
                              _createVNode(_unref(MedKitNumberField), {
                                class: "w-full",
                                placeholder: _unref(t)('presentingComplaint.story.duration.placeholder'),
                                error: fieldErrors[0],
                                label: fieldName,
                                "model-value": duration,
                                "unit-options": periodOptions.map(option => ({
                    label: `${option.label}s`,
                    value: option.value,
                  })),
                                unit: period,
                                required: required,
                                "onUpdate:modelValue": setDuration,
                                "onUpdate:unit": setPeriod,
                                onInput: validateIfAlreadyValidated,
                                onBlur: validate
                              }, null, 8, ["placeholder", "error", "label", "model-value", "unit-options", "unit", "required", "onUpdate:modelValue", "onUpdate:unit", "onInput", "onBlur"])
                            ]),
                            _: 2
                          }, 1032, ["path"]))
                        : _createCommentVNode("", true),
                      (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.episodicity`)))
                        ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                            key: 3,
                            path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.episodicity`)
                          }, {
                            default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                              _createVNode(_unref(MedKitSelect), {
                                class: "w-full",
                                placeholder: _unref(t)('presentingComplaint.story.episodicity.placeholder'),
                                error: fieldErrors[0],
                                label: fieldName,
                                "model-value": selected,
                                options: options,
                                required: required,
                                "onUpdate:modelValue": setSelected,
                                onInput: validate
                              }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                            ]),
                            _: 2
                          }, 1032, ["path"]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (_unref(isAnatomicalLocationARepeater) && !_unref(isBodySiteNameTheOnlyAnatomicalLocationField))
                ? (_openBlock(), _createBlock(_unref(BlockWell), {
                    key: 0,
                    class: "p-6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NodeRepeater), {
                        path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location`)
                      }, {
                        header: _withCtx(({ addItem }) => [
                          _createVNode(_unref(MedKitButton), {
                            class: "w-full",
                            kind: "repeater",
                            label: _unref(t)('presentingComplaint.story.anatomicalLocation.add'),
                            icon: "plus",
                            "icon-position": "start",
                            size: "medium",
                            onClick: addItem
                          }, null, 8, ["label", "onClick"])
                        ]),
                        default: _withCtx(({ index: anatomicalLocationIndex, remove: removeAnatomicalLocation }) => [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex space-x-6", {
                    'mt-6': anatomicalLocationIndex === 0,
                    'border-t mt-8 mb-7 last:mb-0 lg:my-0 pt-6 lg:pt-0 lg:border-t-0': anatomicalLocationIndex > 0
                  }])
                          }, [
                            (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.body_site_name`)))
                              ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                  key: 0,
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.body_site_name`)
                                }, {
                                  default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                    _createVNode(_unref(MedKitSelect), {
                                      class: "flex-1",
                                      placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.bodySiteName.placeholder'),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                      "model-value": selected,
                                      options: options,
                                      required: required,
                                      "onUpdate:modelValue": setSelected,
                                      onInput: validate
                                    }, null, 8, ["placeholder", "error", "label", "label-hidden", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]))
                              : _createCommentVNode("", true),
                            (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.laterality`)))
                              ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                  key: 1,
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.laterality`)
                                }, {
                                  default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                    _createVNode(_unref(MedKitRadioGroup), {
                                      name: `any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.laterality`,
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "model-value": selected,
                                      options: options.map(option => ({
                        label: _unref(t)(`presentingComplaint.story.anatomicalLocation.laterality.options.${option.value}`),
                        value: option.value,
                      })),
                                      contained: "",
                                      required: required,
                                      "onUpdate:modelValue": setSelected,
                                      onInput: validate
                                    }, null, 8, ["name", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]))
                              : _createCommentVNode("", true),
                            (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.aspect`)))
                              ? (_openBlock(), _createBlock(_unref(DvCodedTextList), {
                                  key: 2,
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.aspect`)
                                }, {
                                  default: _withCtx(({ name: fieldName, values, setValues, items, errors: fieldErrors, validate }) => [
                                    _createVNode(_unref(DvCodedTextFilter), { items: items }, {
                                      default: _withCtx(({ filter, results }) => [
                                        _createVNode(_unref(MedKitSearch), {
                                          class: "flex-1",
                                          multiple: "",
                                          "model-value": values.map(value => ({
                          label: value['|value'],
                          value: value,
                        })),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                          options: results.map(result => ({
                          label: result['|value'],
                          value: result,
                        })),
                                          placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.aspect.placeholder'),
                                          "onUpdate:query": filter,
                                          "onUpdate:modelValue": ($event: any) => (setValues($event.map(value => value.value))),
                                          onInput: validate
                                        }, null, 8, ["model-value", "error", "label", "label-hidden", "options", "placeholder", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                                      ]),
                                      _: 2
                                    }, 1032, ["items"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]))
                              : _createCommentVNode("", true),
                            (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.anatomical_line`)))
                              ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                  key: 3,
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.${anatomicalLocationIndex}.anatomical_line`)
                                }, {
                                  default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate }) => [
                                    _createVNode(_unref(MedKitSelect), {
                                      class: "flex-1",
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "label-hidden": display.lgAndUp ? anatomicalLocationIndex > 0 : false,
                                      "model-value": selected,
                                      options: options,
                                      placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.anatomicalLine.placeholder'),
                                      "onUpdate:modelValue": setSelected,
                                      onInput: validate
                                    }, null, 8, ["error", "label", "label-hidden", "model-value", "options", "placeholder", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]))
                              : _createCommentVNode("", true),
                            _createVNode(_unref(MedKitButton), {
                              class: _normalizeClass(["flex-shrink-0 mt-8", {
                      'lg:mt-2': anatomicalLocationIndex > 0
                    }]),
                              label: _unref(t)('presentingComplaint.story.anatomicalLocation.remove'),
                              "icon-position": "only",
                              icon: "delete",
                              onClick: removeAnatomicalLocation
                            }, null, 8, ["class", "label", "onClick"])
                          ], 2)
                        ]),
                        _: 2
                      }, 1032, ["path"])
                    ]),
                    _: 2
                  }, 1024))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (_unref(isAnatomicalLocationARepeater) && _unref(isBodySiteNameTheOnlyAnatomicalLocationField))
                    ? (_openBlock(), _createBlock(_unref(HeadlessRepeater), {
                        key: 0,
                        path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location`)
                      }, {
                        default: _withCtx(({ value, setValue, required: anatomicalLocationRequired }) => [
                          _createVNode(_unref(DvCodedText), {
                            path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.body_site_name`)
                          }, {
                            default: _withCtx(({ name: fieldName, items, required: bodySiteRequired }) => [
                              _createVNode(_unref(DvCodedTextFilter), { items: items }, {
                                default: _withCtx(({ filter, results }) => [
                                  _createVNode(_unref(MedKitSearch), {
                                    class: "col-span-2",
                                    multiple: "",
                                    "min-search-length": 1,
                                    "model-value": value.map(item => ({
                        label: item.body_site_name['|value'],
                        value: item.body_site_name,
                      })),
                                    label: fieldName,
                                    options: results.map(result => ({
                        label: result['|value'],
                        value: result,
                      })),
                                    placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.bodySiteName.multiple_placeholder'),
                                    required: anatomicalLocationRequired && bodySiteRequired,
                                    "onUpdate:query": filter,
                                    "onUpdate:modelValue": ($event: any) => (setValue($event.map(item => ({
                        body_site_name: item.value
                      }))))
                                  }, null, 8, ["model-value", "label", "options", "placeholder", "required", "onUpdate:query", "onUpdate:modelValue"])
                                ]),
                                _: 2
                              }, 1032, ["items"])
                            ]),
                            _: 2
                          }, 1032, ["path"])
                        ]),
                        _: 2
                      }, 1032, ["path"]))
                    : _createCommentVNode("", true),
                  (!_unref(isAnatomicalLocationARepeater))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.body_site_name`)))
                          ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                              key: 0,
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.body_site_name`)
                            }, {
                              default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(MedKitSelect), {
                                  class: _normalizeClass([ !_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.laterality`)) && !_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.aspect`)) ? 'col-span-2' : 'col-span-1' ]),
                                  placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.bodySiteName.placeholder'),
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": selected,
                                  options: options,
                                  required: required,
                                  "onUpdate:modelValue": setSelected,
                                  onInput: validate
                                }, null, 8, ["class", "placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["path"]))
                          : _createCommentVNode("", true),
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.laterality`)))
                          ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                              key: 1,
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.laterality`)
                            }, {
                              default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(MedKitRadioGroup), {
                                  name: `any_event.${anyEventIndex}.symptom_sign.anatomical_location.laterality`,
                                  class: "col-span-1",
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": selected,
                                  options: options.map(option => ({
                      label: _unref(t)(`presentingComplaint.story.anatomicalLocation.laterality.options.${option.value}`),
                      value: option.value,
                    })),
                                  contained: "",
                                  required: required,
                                  "onUpdate:modelValue": setSelected,
                                  onInput: validate
                                }, null, 8, ["name", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["path"]))
                          : _createCommentVNode("", true),
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.aspect`)))
                          ? (_openBlock(), _createBlock(_unref(DvCodedTextList), {
                              key: 2,
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.aspect`)
                            }, {
                              default: _withCtx(({ name: fieldName, values, setValues, items, errors: fieldErrors, validate }) => [
                                _createVNode(_unref(DvCodedTextFilter), { items: items }, {
                                  default: _withCtx(({ filter, results }) => [
                                    _createVNode(_unref(MedKitSearch), {
                                      class: "col-span-1",
                                      multiple: "",
                                      "model-value": values.map(value => ({
                        label: value['|value'],
                        value: value,
                      })),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      options: results.map(result => ({
                        label: result['|value'],
                        value: result,
                      })),
                                      placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.aspect.placeholder'),
                                      "onUpdate:query": filter,
                                      "onUpdate:modelValue": ($event: any) => (setValues($event.map(value => value.value))),
                                      onInput: validate
                                    }, null, 8, ["model-value", "error", "label", "options", "placeholder", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["items"])
                              ]),
                              _: 2
                            }, 1032, ["path"]))
                          : _createCommentVNode("", true),
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.anatomical_line`)))
                          ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                              key: 3,
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.anatomical_location.anatomical_line`)
                            }, {
                              default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate }) => [
                                _createVNode(_unref(MedKitSelect), {
                                  class: "col-span-1",
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": selected,
                                  options: options,
                                  placeholder: _unref(t)('presentingComplaint.story.anatomicalLocation.anatomicalLine.placeholder'),
                                  "onUpdate:modelValue": setSelected,
                                  onInput: validate
                                }, null, 8, ["error", "label", "model-value", "options", "placeholder", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["path"]))
                          : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true),
                  (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.overall_comment`)))
                    ? (_openBlock(), _createBlock(_unref(DvText), {
                        key: 2,
                        path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.overall_comment`)
                      }, {
                        default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                          _createVNode(_unref(MedKitTextField), {
                            class: _normalizeClass({
                    'col-span-2': !_unref(isAnatomicalLocationARepeater) || (_unref(isAnatomicalLocationARepeater) && _unref(isBodySiteNameTheOnlyAnatomicalLocationField)),
                    'col-span-4': _unref(isAnatomicalLocationARepeater) && !_unref(isBodySiteNameTheOnlyAnatomicalLocationField)
                  }),
                            placeholder: _unref(t)('presentingComplaint.story.overallComment.placeholder'),
                            error: fieldErrors[0],
                            label: fieldName,
                            "model-value": value,
                            multiline: "",
                            required: required,
                            "onUpdate:modelValue": setValue,
                            onInput: validateIfAlreadyValidated,
                            onBlur: validate
                          }, null, 8, ["class", "placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                        ]),
                        _: 2
                      }, 1032, ["path"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              (hasSomeNodes(anyEventIndex, ['occurrence', 'number_of_previous_episodes', 'description_of_previous_episodes']))
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)('presentingComplaint.story.previousEpisodes.title')), 1),
                      _createElementVNode("div", _hoisted_11, [
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.occurrence`)))
                          ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                              key: 0,
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.occurrence`)
                            }, {
                              default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(MedKitSelect), {
                                  class: "col-span-1",
                                  placeholder: _unref(t)('presentingComplaint.story.previousEpisodes.occurrence.placeholder'),
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": selected,
                                  options: options,
                                  required: required,
                                  "onUpdate:modelValue": setSelected,
                                  onInput: validate
                                }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["path"]))
                          : _createCommentVNode("", true),
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.number_of_previous_episodes`)))
                          ? (_openBlock(), _createBlock(_unref(DvCount), {
                              key: 1,
                              "present-if": _unref(getData)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.occurrence`))?.['|code'] === '263853000',
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.number_of_previous_episodes`)
                            }, {
                              default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, required }) => [
                                _createVNode(_unref(MedKitNumberField), {
                                  class: "col-span-1",
                                  placeholder: _unref(t)('presentingComplaint.story.previousEpisodes.numberOfPreviousEpisodes.placeholder'),
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": value,
                                  "unit-options": [{ label: 'episodes', value: 'episodes' }],
                                  required: required,
                                  "onUpdate:modelValue": setValue,
                                  onInput: validate
                                }, null, 8, ["placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput"])
                              ]),
                              _: 2
                            }, 1032, ["present-if", "path"]))
                          : _createCommentVNode("", true),
                        (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.description_of_previous_episodes`)))
                          ? (_openBlock(), _createBlock(_unref(DvText), {
                              key: 2,
                              "present-if": _unref(getData)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.occurrence`))?.['|code'] === '263853000',
                              path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.description_of_previous_episodes`)
                            }, {
                              default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                                _createVNode(_unref(MedKitTextField), {
                                  class: "col-span-2",
                                  placeholder: _unref(t)('presentingComplaint.story.previousEpisodes.descriptionOfPreviousEpisodes.placeholder'),
                                  error: fieldErrors[0],
                                  label: fieldName,
                                  "model-value": value,
                                  required: required,
                                  multiline: "",
                                  "onUpdate:modelValue": setValue,
                                  onInput: validateIfAlreadyValidated,
                                  onBlur: validate
                                }, null, 8, ["placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                              ]),
                              _: 2
                            }, 1032, ["present-if", "path"]))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_12, [
                (getFactorTypeNodes(anyEventIndex).length > 0)
                  ? (_openBlock(), _createBlock(_unref(MultiNodeRepeater), {
                      key: 0,
                      class: "border-b border-gray-300",
                      reverse: "",
                      items: getFactorTypeNodes(anyEventIndex)
                    }, {
                      header: _withCtx(({ headerItems }) => [
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(t)('presentingComplaint.story.additionalContext.title')), 1),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerItems, (headerItem, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: index,
                              class: "w-full md:w-fit flex"
                            }, [
                              (headerItem.type === factorTypes.associatedSymptoms)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_15))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", {
                                class: _normalizeClass(["border-b-4 mb-2 md:mb-0 md:pb-4 w-full md:w-fit", [getFactorTypeBorderColour(headerItem.type)]])
                              }, [
                                _createVNode(_unref(MedKitButton), {
                                  icon: "plus",
                                  size: "small",
                                  class: "w-full",
                                  "icon-position": "start",
                                  label: headerItem.label,
                                  onClick: headerItem.addItem
                                }, null, 8, ["label", "onClick"])
                              ], 2)
                            ]))
                          }), 128))
                        ])
                      ]),
                      default: _withCtx(({itemType, index, nodeIndex, removeItem}) => [
                        _createElementVNode("div", {
                          class: _normalizeClass(["border border-1 border-l-8 p-4 bg-white flex gap-x-6 rounded-lg drop-shadow-md mx-6 my-6 relative", [getFactorTypeBorderColour(itemType)]]),
                          style: _normalizeStyle({ 'zIndex': (index + 1) })
                        }, [
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.precipitating_factor`)) && itemType == factorTypes.precipitatingFactor)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _createVNode(_unref(DvCodedText), {
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.precipitating_factor.${nodeIndex}.precipitating_factor`)
                                }, {
                                  default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                    _createVNode(_unref(MedKitSelect), {
                                      class: "col-span-12 md:col-span-4",
                                      placeholder: _unref(t)('presentingComplaint.story.additionalContext.precipitatingFactor.precipitating_factor.placeholder'),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "model-value": selected,
                                      options: options,
                                      required: required,
                                      "onUpdate:modelValue": setSelected,
                                      onInput: validate
                                    }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]),
                                _createVNode(_unref(DvText), {
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.precipitating_factor.${nodeIndex}.description`)
                                }, {
                                  default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                                    _createVNode(_unref(MedKitTextField), {
                                      class: "col-span-12 md:col-span-8",
                                      placeholder: _unref(t)('presentingComplaint.story.additionalContext.precipitatingFactor.description.placeholder'),
                                      multiline: true,
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "model-value": value,
                                      required: required,
                                      "onUpdate:modelValue": setValue,
                                      onInput: validateIfAlreadyValidated,
                                      onBlur: validate
                                    }, null, 8, ["placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"])
                              ]))
                            : _createCommentVNode("", true),
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor`)) && itemType == factorTypes.modifyingFactor)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.modifying_factor`)))
                                  ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                      key: 0,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.modifying_factor`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                        _createVNode(_unref(MedKitSelect), {
                                          class: "col-span-12 md:col-span-3",
                                          placeholder: _unref(t)('presentingComplaint.story.additionalContext.modifyingFactor.modifying_factor.placeholder'),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "model-value": selected,
                                          options: options,
                                          required: required,
                                          "onUpdate:modelValue": setSelected,
                                          onInput: validate
                                        }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true),
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.effect`)))
                                  ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                      key: 1,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.effect`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                        _createVNode(_unref(MedKitSelect), {
                                          class: "col-span-12 md:col-span-3",
                                          placeholder: _unref(t)('presentingComplaint.story.additionalContext.modifyingFactor.effect.placeholder'),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "model-value": selected,
                                          options: options,
                                          required: required,
                                          "onUpdate:modelValue": setSelected,
                                          onInput: validate
                                        }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true),
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.description`)))
                                  ? (_openBlock(), _createBlock(_unref(DvText), {
                                      key: 2,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.modifying_factor.${nodeIndex}.description`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required}) => [
                                        _createVNode(_unref(MedKitTextField), {
                                          class: "col-span-12 md:col-span-6",
                                          multiline: "",
                                          placeholder: _unref(t)('presentingComplaint.story.additionalContext.modifyingFactor.description.placeholder'),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "model-value": value,
                                          required: required,
                                          "onUpdate:modelValue": setValue,
                                          onInput: validateIfAlreadyValidated,
                                          onBlur: validate
                                        }, null, 8, ["placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true),
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.resolving_factor`)) && itemType == factorTypes.resolvingFactor)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                _createVNode(_unref(DvCodedText), {
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.resolving_factor.${nodeIndex}.resolving_factor`)
                                }, {
                                  default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                    _createVNode(_unref(MedKitSelect), {
                                      class: "col-span-12 md:col-span-4",
                                      placeholder: _unref(t)('presentingComplaint.story.additionalContext.resolvingFactor.resolving_factor.placeholder'),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "model-value": selected,
                                      options: options,
                                      required: required,
                                      "onUpdate:modelValue": setSelected,
                                      onInput: validate
                                    }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"]),
                                _createVNode(_unref(DvText), {
                                  path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.resolving_factor.${nodeIndex}.description`)
                                }, {
                                  default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                                    _createVNode(_unref(MedKitTextField), {
                                      class: "col-span-12 md:col-span-8",
                                      multiline: "",
                                      placeholder: _unref(t)('presentingComplaint.story.additionalContext.resolvingFactor.description.placeholder'),
                                      error: fieldErrors[0],
                                      label: fieldName,
                                      "model-value": value,
                                      required: required,
                                      "onUpdate:modelValue": setValue,
                                      onInput: validateIfAlreadyValidated,
                                      onBlur: validate
                                    }, null, 8, ["placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                                  ]),
                                  _: 2
                                }, 1032, ["path"])
                              ]))
                            : _createCommentVNode("", true),
                          (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign`)) && itemType == factorTypes.associatedSymptoms)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.symptom_sign_name`)))
                                  ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                      key: 0,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.symptom_sign_name`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, options, selected, setSelected, errors: fieldErrors, validate, required }) => [
                                        _createVNode(_unref(MedKitSelect), {
                                          class: "col-span-12 md:col-span-3",
                                          placeholder: _unref(t)('presentingComplaint.story.additionalContext.associatedSymptoms.symptomSignName.placeholder'),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "model-value": selected,
                                          options: options,
                                          required: required,
                                          "onUpdate:modelValue": setSelected,
                                          onInput: validate
                                        }, null, 8, ["placeholder", "error", "label", "model-value", "options", "required", "onUpdate:modelValue", "onInput"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true),
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.anatomical_location.body_site_name`)))
                                  ? (_openBlock(), _createBlock(_unref(DvCodedText), {
                                      key: 1,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.anatomical_location.body_site_name`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, terminology, value, setValue, errors: fieldErrors, validate, required }) => [
                                        _createVNode(_unref(TerminologySearch), { terminology: terminology }, {
                                          default: _withCtx(({ search, searching, results, error: terminologySearchError }) => [
                                            _createVNode(_unref(MedKitSearch), {
                                              class: "col-span-12 md:col-span-3",
                                              placeholder: _unref(t)('presentingComplaint.story.additionalContext.associatedSymptoms.bodySiteName.placeholder'),
                                              "model-value": value ? { label: value['|value'], value: value } : undefined,
                                              error: terminologySearchError ?? fieldErrors[0],
                                              label: fieldName,
                                              "is-loading": searching,
                                              options: results.map(result => ({
                            label: result['|value'],
                            value: result,
                          })),
                                              required: required,
                                              "onUpdate:query": search,
                                              "onUpdate:modelValue": ($event: any) => (setValue($event ? $event.value : undefined)),
                                              onInput: validate
                                            }, null, 8, ["placeholder", "model-value", "error", "label", "is-loading", "options", "required", "onUpdate:query", "onUpdate:modelValue", "onInput"])
                                          ]),
                                          _: 2
                                        }, 1032, ["terminology"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true),
                                (_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.comment`)))
                                  ? (_openBlock(), _createBlock(_unref(DvText), {
                                      key: 2,
                                      path: props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.comment`)
                                    }, {
                                      default: _withCtx(({ name: fieldName, value, setValue, errors: fieldErrors, validate, validateIfAlreadyValidated, required }) => [
                                        _createVNode(_unref(MedKitTextField), {
                                          class: _normalizeClass(["col-span-12", {
                          'md:col-span-6': _unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.symptom_sign_name`)) && _unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.anatomical_location.body_site_name`)),
                          'md:col-span-9': !_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.symptom_sign_name`)) || !_unref(nodeExists)(props.path.add(`any_event.${anyEventIndex}.symptom_sign.symptom_sign.${nodeIndex}.anatomical_location.body_site_name`)),
                        }]),
                                          placeholder: _unref(t)('presentingComplaint.story.additionalContext.associatedSymptoms.comment.placeholder'),
                                          error: fieldErrors[0],
                                          label: fieldName,
                                          "model-value": value,
                                          required: required,
                                          multiline: "",
                                          "onUpdate:modelValue": setValue,
                                          onInput: validateIfAlreadyValidated,
                                          onBlur: validate
                                        }, null, 8, ["class", "placeholder", "error", "label", "model-value", "required", "onUpdate:modelValue", "onInput", "onBlur"])
                                      ]),
                                      _: 2
                                    }, 1032, ["path"]))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true),
                          _createVNode(_unref(MedKitButton), {
                            class: "shrink-0 mt-8",
                            label: _unref(t)('presentingComplaint.story.additionalContext.remove'),
                            "icon-position": "only",
                            icon: "delete",
                            onClick: ($event: any) => (removeItem(index))
                          }, null, 8, ["label", "onClick"])
                        ], 6)
                      ]),
                      _: 2
                    }, 1032, ["items"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 2
          }, 1024)
        ]),
        _: 2
      }, 1032, ["path"])
    ]),
    _: 1
  }))
}
}

})